import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'

/*
 * Each city row draws its score with a Chart.js doughnut, and a chart instance
 * plus its canvas is expensive enough that mounting one per city locks up the
 * main thread on large subscriptions (Safari especially). The full list still
 * renders, so scrolling and search behave normally, but a row only builds its
 * chart once it actually comes into view.
 *
 * Once a row has been seen, its chart is kept: scrolling back over earlier
 * cities should not pay the cost again, and only what has genuinely been
 * looked at is ever built.
 */
const LazyDonut = ({ size, render, eager = false }) => {
  const containerRef = useRef(null)
  const [hasBeenVisible, setHasBeenVisible] = useState(eager)

  useEffect(() => {
    if (hasBeenVisible) return undefined

    const node = containerRef.current
    if (!node) return undefined

    if (typeof IntersectionObserver === 'undefined') {
      setHasBeenVisible(true)
      return undefined
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasBeenVisible(true)
          observer.disconnect()
        }
      },
      {
        /*
         * Start a little before the row reaches the edge so the chart is
         * usually ready by the time it is actually looked at.
         */
        rootMargin: '150px',
        threshold: 0,
      }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [hasBeenVisible])

  return (
    <div
      ref={containerRef}
      style={{ width: `${size}px`, height: `${size}px`, margin: '10px auto' }}
    >
      {hasBeenVisible ? (
        render()
      ) : (
        <div
          className="appbarCityMenuDonutPlaceholder"
          style={{
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: '50%',
            border: '3px solid rgba(255, 255, 255, 0.15)',
            boxSizing: 'border-box',
          }}
        />
      )}
    </div>
  )
}

LazyDonut.propTypes = {
  /*
   * Rows that are on screen the moment the menu opens draw immediately, so the
   * list never appears empty while waiting on an observer callback.
   */
  eager: PropTypes.bool,
  render: PropTypes.func.isRequired,
  size: PropTypes.number.isRequired,
}

export default LazyDonut
